import { centralizedAuth } from '@cimpress/simple-auth-wrapper';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CometSubHeader,
  type AppLinkType as AppLinkModel,
} from '@cimpress-technology/comet-platform-sub-header';
import { FulfillerModalConfig, useFulfiller } from '../FulfillerContext';
import { retrieveFulfillerList } from '../utilities/fulfillerHelper';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { isInCometPortal } from '@/utilities/helper';
import LoginAvatar from './common/LoginAvatar';
import TenantSelector from './common/TenantSelector';
import { AppDispatch, RootState } from '@/store/store';
import { useDispatch, useSelector } from 'react-redux';
import { setPermissions } from '@/store/permissionSlice';
import { FulfillerItem, SelectedFulfillerConfig } from '@/models/HeaderTypes';
import { setTimeZone } from '@/store/timeZoneSlice';

type AppHeaderProps = {
  auth: centralizedAuth;
};

const AppHeader = (props: AppHeaderProps) => {
  const navigate = useNavigate();
  const { auth } = props;
  const dispatch: AppDispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const tenant = searchParams.get('tenantId');
  const timezone = useSelector((state: RootState) => state.timeZone);

  const { fulfillerData, xCometAccountId, setFulfillerDetails } =
    useFulfiller();
  const [fulfillerList, setFulfillerList] = useState<FulfillerItem[]>([]);

  // Memoized selection of the currently selected fulfiller.
  const currentFulfillerSelection: FulfillerModalConfig | null = useMemo(
    () =>
      fulfillerData && timezone && xCometAccountId
        ? { fulfillerData, timezone, xCometAccountId }
        : null,
    [fulfillerData, timezone, xCometAccountId],
  );

  useEffect(() => {
    // Fetches the fulfiller data asynchronously and updates state.
    const fetchStateFulfillerData = async () => {
      try {
        const fulfillerObj = await retrieveFulfillerList();
        setFulfillerList(fulfillerObj);
      } catch (error) {
        console.error('Error fetching fulfiller list:', error);
        setFulfillerList([]);
      }
    };
    fetchStateFulfillerData();
  }, [tenant]);

  useEffect(() => {
    if (!tenant || !fulfillerList.length || !fulfillerData) return;

    if (tenant !== fulfillerData?.fulfillerLocation.value) {
      let matchedConfig: SelectedFulfillerConfig | undefined;

      for (const fulfillerItem of fulfillerList) {
        const matchedLocation = fulfillerItem.fulfillerLocations.find(
          (loc) => loc.value === tenant,
        );

        if (matchedLocation) {
          matchedConfig = {
            fulfiller: fulfillerItem.fulfiller,
            fulfillerLocation: matchedLocation,
            xCometAccountId: fulfillerItem.xCometAccountId,
          };
          break;
        }
      }

      if (matchedConfig) {
        setFulfillerDetails({
          fulfillerData: matchedConfig,
          xCometAccountId: matchedConfig.xCometAccountId,
        });
      }
    }
  }, [tenant, fulfillerList, fulfillerData, setFulfillerDetails]);

  const handleFulfillerSelection = useCallback(
    (selectedFulfillerConfig: SelectedFulfillerConfig | null) => {
      if (!selectedFulfillerConfig) return;

      if (
        currentFulfillerSelection &&
        isEqual(
          selectedFulfillerConfig,
          currentFulfillerSelection.fulfillerData,
        )
      ) {
        // If fulfiller didn't change, no need to update state or navigate
        return;
      }

      const activeTenantConfig: SelectedFulfillerConfig = {
        fulfiller: selectedFulfillerConfig.fulfiller,
        fulfillerLocation: selectedFulfillerConfig.fulfillerLocation,
        xCometAccountId: selectedFulfillerConfig.xCometAccountId,
      };
      setFulfillerDetails({
        fulfillerData: activeTenantConfig,
        xCometAccountId: activeTenantConfig.xCometAccountId,
      });

      const filfillerChanged =
        !fulfillerData ||
        fulfillerData.fulfiller.value !== activeTenantConfig.fulfiller.value ||
        fulfillerData.fulfillerLocation.value !==
          activeTenantConfig.fulfillerLocation.value;

      if (!filfillerChanged) {
        // If fulfiller didn't change, no need to navigate or update state
        return;
      }

      navigate(
        `/entitytypes?tenantId=${selectedFulfillerConfig.fulfillerLocation.value}`,
        { replace: true },
      );
    },
    [],
  );

  const handleTimezoneSelection = useCallback(
    (tz: typeof timezone) => {
      dispatch(setTimeZone(tz));
    },
    [dispatch],
  );

  const handleUserLogout = useCallback(() => {
    auth.logout('/');
    dispatch(setPermissions({ read: false, create: false, update: false }));
  }, [dispatch]);

  const renderAppLinks = useMemo((): AppLinkModel[] => {
    const links: AppLinkModel[] = [
      {
        title: 'Entity Manager',
        module: 'Entity Manager',
        type: 'link',
        content: (
          <NavLink
            to={
              currentFulfillerSelection
                ? `/entitytypes?tenantId=${currentFulfillerSelection.fulfillerData.fulfillerLocation.value}`
                : `/`
            }
          >
            Entity Manager
          </NavLink>
        ),
        isActive: location.pathname.startsWith(`/`),
      },
    ];

    if (!isInCometPortal() && fulfillerList.length > 0) {
      links.push({
        module: 'tenant-selector',
        type: 'custom',
        content: (
          <TenantSelector
            fulfillerList={fulfillerList}
            selectedFulfillerConfig={fulfillerData}
            handleFulfillerSelection={handleFulfillerSelection}
            handleTimezoneSelection={handleTimezoneSelection}
            selectedCometAccountId={xCometAccountId}
            selectedTimezone={timezone}
          />
        ),
      });

      links.push({
        module: 'login-avatar',
        type: 'custom',
        content: (
          <LoginAvatar
            isUserLoggedIn={auth.isLoggedIn()}
            onLogout={handleUserLogout}
            profile={auth.getProfile()}
          />
        ),
      });
    }
    return links;
  }, [
    auth,
    currentFulfillerSelection,
    fulfillerData,
    fulfillerList,
    handleUserLogout,
    location.pathname,
    timezone,
  ]);

  return (
    <CometSubHeader
      appLinks={renderAppLinks}
      key={fulfillerData?.fulfiller.value}
    />
  );
};

export default AppHeader;
